import isEqual from 'lodash/isEqual';
import {
    BooleanAttribute,
    NumberAttribute,
    WatermarkAttribute,
    getAttributesToSerialize,
} from '../attributes';

import { WatermarkPositions } from '../../enums';
import { TimeInvariantBase } from '../TimeInvariantBase';

import { Config } from '../../video-data/config';
import type { AspectRatio } from '../../aspectRatioConfig';
import { ALL_ASPECT_RATIOS } from '../../aspectRatioConfig';
import type { MediaSourceData } from '../asset/MediaAsset';
import type { WatermarkThemeRef } from '../attributes/template/common';
import { assertArray, assertInArray, assertMinimumLength } from '../../validators';
import type { Template } from '../template/Template';
import type { FillMode } from '../../video-data/types';
import type { BaseSettings } from '../TimeAwareBase';
import { WatermarkLayout } from './WatermarkLayout';
import { observable } from '../reactivity';

export type TemplateWatermarkManagerSettings = BaseSettings & {
    getAspectRatio: () => AspectRatio;
};

/**
 * This class contains all watermark settings that can be set on the template level.
 * These settings can be overridden by a scene using its SceneWatermarkSettings.
 */
export class TemplateWatermarkManager extends TimeInvariantBase {
    override _classType: string = 'TemplateWatermarkManager';
    override name = 'TemplateWatermarkManager';

    @observable accessor #position: WatermarkPositions = WatermarkPositions.TopRight;
    @WatermarkAttribute()
    @observable
    accessor sourceData: MediaSourceData | typeof Config.WATERMARK_NONE | WatermarkThemeRef =
        Config.WATERMARK_NONE;
    @NumberAttribute({ min: 0, max: 1 })
    @observable
    accessor opacity = 1;
    @BooleanAttribute()
    @observable
    accessor visible = true;
    @BooleanAttribute()
    accessor isEditable = true;

    @observable accessor #fillMode: FillMode = Config.FILL_AREA;
    #getAspectRatio: () => AspectRatio;

    constructor(settings: TemplateWatermarkManagerSettings) {
        super(settings);
        this.#getAspectRatio = settings.getAspectRatio;
    }

    #layouts: Map<AspectRatio, WatermarkLayout> = new Map(
        ALL_ASPECT_RATIOS.map((aspectRatio) => [
            aspectRatio,
            new WatermarkLayout({ parent: this, fetchResources: false }),
        ]),
    );

    getLayout(aspectRatio: AspectRatio): WatermarkLayout {
        return this.#layouts.get(aspectRatio)!;
    }

    @observable.ref accessor #availablePositions: WatermarkPositions[] = Config.WATERMARK_POSITIONS;

    /**
     * The position (corner) of the watermark
     */
    get position(): WatermarkPositions {
        return this.#position;
    }

    set position(position: WatermarkPositions) {
        if (this.#position === position) {
            return;
        }
        assertInArray(position, this.availablePositions);
        this.#position = position;
        this.requiresUpdate = true;
    }

    /**
     * List of allowed watermark position for all scenes in video/template
     */
    get availablePositions(): WatermarkPositions[] {
        return this.#availablePositions.slice();
    }

    set availablePositions(availablePositions: readonly WatermarkPositions[]) {
        if (isEqual(this.#availablePositions, availablePositions)) {
            return;
        }
        assertArray(availablePositions);
        assertMinimumLength(availablePositions, 1);
        assertInArray(this.position, availablePositions);
        availablePositions.forEach((position) =>
            assertInArray(position, Config.WATERMARK_POSITIONS),
        );
        this.#availablePositions = availablePositions.slice();
        this.requiresUpdate = true;
    }

    _availablePositionsCustomLoad(positions: WatermarkPositions[]) {
        // Custom load is required as default position is `top_right`, and the `set availablePositions()` will have
        // failing assertions during load if the `availablePositions` does not include the default `top_right`.
        this.#availablePositions = positions;
    }

    _layoutsCustomLoad(layouts: Record<AspectRatio, Record<string, number>>) {
        Object.entries(layouts).forEach(([aspectRatio, layout]) => {
            this.#layouts.get(aspectRatio as AspectRatio)!.load(layout);
        });
    }

    _layoutsCustomSave() {
        const result: Record<AspectRatio, Record<string, number>> = {} as Record<
            AspectRatio,
            Record<string, number>
        >;
        this.#layouts.forEach((layout, aspectRatio) => {
            result[aspectRatio] = layout.save();
        });
        return result;
    }

    /**
     * Gets the layout (margins & watermark max size) applicable to the current aspect ratio.
     */
    get activeLayout(): WatermarkLayout {
        return this.#layouts.get(this.#getAspectRatio())!;
    }

    set fillMode(value) {
        if (value === this.fillMode) {
            return;
        }
        assertInArray(value, Config.ALL_FILL_MODES);
        this.#fillMode = value;
        this.requiresUpdate = true;
    }

    get fillMode() {
        return this.#fillMode;
    }

    override clear() {
        super.clear();
        this.sourceData = Config.WATERMARK_NONE;
        this.availablePositions = Config.WATERMARK_POSITIONS;
        this.position = Config.WATERMARK_TOP_RIGHT_POSITION;
        this.opacity = Config.WATERMARK_DEFAULT_OPACITY;
        // Layout:
        // We don't clear the layout, since users can't change these
        // and we don't want to lose to the template's values.
    }

    override get propertiesToSerialize() {
        return super.propertiesToSerialize
            .concat(['availablePositions', 'layouts', 'position', 'fillMode'])
            .concat(getAttributesToSerialize(this));
    }

    override get template(): Template {
        return this.parent as Template;
    }
}
